// cart-entries-estimate.js
(function () {
  const UPDATE_DEBOUNCE_MS = 350;
  const REVALIDATE_MS = 60000;

  function debounce(fn, wait) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function getSupabaseConfig() {
    const themeSettings = window.Shopify?.theme?.settings;
    if (themeSettings?.supabase_url && themeSettings?.supabase_anon_key) {
      return {
        url: themeSettings.supabase_url,
        anonKey: themeSettings.supabase_anon_key,
      };
    }
    if (window.SUPABASE_CONFIG?.url && window.SUPABASE_CONFIG?.anonKey) {
      return {
        url: window.SUPABASE_CONFIG.url,
        anonKey: window.SUPABASE_CONFIG.anonKey,
      };
    }
    return null;
  }

  class CartEntriesEstimate {
    constructor(config) {
      this.config = config;
      this.promotionCache = new Map(); // NEW: Cache for product promotions
      this.cacheExpiry = 5 * 60 * 1000; // NEW: 5 minute expiry
      this.lastCartSignature = "";
      this.lastEstimateSignature = "";
      this.lastEstimateData = null;
      this.inFlight = false;
      this.cartDirty = true;
      this.refreshQueued = false;
      this.cartRevision = 0;
      this.lastValidatedAt = 0;
      this.observedRoots = new WeakSet();
      this.raffleCache = null; // { state: 'active'|'none', ts } — 60s TTL; 'error' is NEVER cached
      this.debouncedRefresh = debounce(
        () => this.refresh(),
        UPDATE_DEBOUNCE_MS,
      );
    }

    init() {
      this.setupObservers();
      this.debouncedRefresh();
      // Revalidate time-sensitive promotions only while the estimate is visible.
      setInterval(() => {
        if (!this.isVisible()) return;
        this.lastValidatedAt = 0;
        this.invalidateCart();
        this.debouncedRefresh();
      }, REVALIDATE_MS);
    }

    isVisible() {
      if (document.visibilityState === "hidden") return false;
      return this.getSummaryTargets().some((target) => {
        const drawer = target.closest("cart-drawer-component");
        return drawer ? !!drawer.querySelector("dialog[open]") : target.getClientRects().length > 0;
      });
    }

    observeCartRoots() {
      document.querySelectorAll("cart-drawer-component, cart-items-component, .cart__summary-totals").forEach((root) => {
        if (this.observedRoots.has(root)) return;
        this.observedRoots.add(root);
        new MutationObserver((records) => {
          // Ignore our own numbers/notes, but discover roots after native morphs.
          const owned = ".kaido-cart-entries-total-row, .kaido-cart-line-entries-inline, .kaido-cart-line-entries-slot, .kaido-cart-line-entries, .kaido-cart-entries-paused-note";
          const isOwned = (node) => node.nodeType === 1 && node.matches(owned);
          if (!records.some((record) => !record.target.closest?.(owned) &&
            ![...record.addedNodes, ...record.removedNodes].every(isOwned))) return;
          this.observeCartRoots();
          this.debouncedRefresh();
        }).observe(root, {
          childList: true, subtree: true,
        });
      });
    }

    invalidateCart() {
      this.cartDirty = true;
      this.cartRevision++;
    }

    setupObservers() {
      this.observeCartRoots();
      document.addEventListener("kaido:public-snapshot", () => {
        this.lastValidatedAt = 0;
        this.promotionCache.clear();
        this.baseRatePromise = null;
        this.lastCartSignature = "";
        this.invalidateCart();
        this.debouncedRefresh();
      });
      // Native cart events fire after the successful Shopify mutation.
      for (const name of ["cart:update", "discount:update"]) {
        document.addEventListener(name, (event) => {
          if (event.detail?.data?.didError) return;
          this.invalidateCart();
          this.observeCartRoots();
          this.debouncedRefresh();
        });
      }
      document.addEventListener("dialog:open", (event) => {
        if (!event.target.closest?.("cart-drawer-component")) return;
        this.invalidateCart();
        this.observeCartRoots();
        this.debouncedRefresh();
      }, true);
      document.addEventListener("shopify:section:load", () => {
        this.observeCartRoots();
        this.invalidateCart();
        this.debouncedRefresh();
      });
      document.addEventListener("visibilitychange", () => {
        if (!this.isVisible()) return;
        this.invalidateCart();
        this.debouncedRefresh();
      });
    }

    async fetchCart() {
      const res = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
      return res.json();
    }

    getPreDiscountLineSubtotalCents(item) {
      const qty = Number(item?.quantity || 0);
      const originalLinePrice = Number(item?.original_line_price);
      const originalUnitPrice = Number(item?.original_price);
      const linePrice = Number(item?.line_price);
      const finalLinePrice = Number(item?.final_line_price);

      const candidates = [];
      if (Number.isFinite(originalLinePrice) && originalLinePrice >= 0) {
        candidates.push(Math.round(originalLinePrice));
      }
      if (
        Number.isFinite(originalUnitPrice) &&
        originalUnitPrice >= 0 &&
        Number.isFinite(qty) &&
        qty >= 0
      ) {
        candidates.push(Math.round(originalUnitPrice * qty));
      }
      if (Number.isFinite(linePrice) && linePrice >= 0) {
        candidates.push(Math.round(linePrice));
      }
      if (Number.isFinite(finalLinePrice) && finalLinePrice >= 0) {
        candidates.push(Math.round(finalLinePrice));
      }
      if (!candidates.length) return 0;
      return Math.max(...candidates);
    }

    buildPayload(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return {
        items: items.map((item) => ({
          line_item_id: item.key,
          product_id: String(item.product_id),
          variant_id: item.variant_id != null ? String(item.variant_id) : null,
          quantity: Number(item.quantity || 0),
          // Entries are based on pre-discount merchandise value.
          line_subtotal_cents: this.getPreDiscountLineSubtotalCents(item),
        })),
      };
    }

    cartSignature(payload) {
      return JSON.stringify(
        (payload?.items || []).map((i) => [
          i.line_item_id,
          i.product_id,
          i.quantity,
          i.line_subtotal_cents,
        ]),
      );
    }

    // === NEW LOGIC: Copied from sweepstakes-entries.js ===
    roundUpTo10(n) {
      return Math.ceil(n / 10) * 10;
    }

    async fetchBaseRate() {
      if (window.KaidoPublicSnapshot) {
        await window.KaidoPublicSnapshot.ready;
        const rate = window.KaidoPublicSnapshot.rate();
        if (!Number.isFinite(rate) || rate <= 0) throw new Error('Public rate unavailable');
        return rate;
      }
      if (this.baseRatePromise) return this.baseRatePromise;
      this.baseRatePromise = (async () => {
        const { url, anonKey } = this.config;
        const headers = { apikey: anonKey, Authorization: `Bearer ${anonKey}` };
        
        // Attempt 1: bonus window edge function (e.g., 300X weekend)
        try {
          const res = await fetch(`${url}/functions/v1/get_active_bonus_window`, { headers });
          if (res.ok) {
            const data = await res.json();
            if (data?.current?.display_badge) {
              const match = String(data.current.display_badge).match(/(\d+)/);
              if (match) {
                const n = parseInt(match[1], 10);
                if (Number.isFinite(n) && n > 0) return n;
              }
            }
          }
        } catch (_) {}

        // Attempt 2: raffle_settings table
        try {
          const res = await fetch(`${url}/rest/v1/raffle_settings?setting_key=eq.entries_per_dollar&select=setting_value&limit=1`, { headers });
          if (res.ok) {
            const rows = await res.json();
            if (rows?.[0]?.setting_value) {
              const n = parseInt(rows[0].setting_value, 10);
              if (Number.isFinite(n) && n > 0) return n;
            }
          }
        } catch (_) {}

        return window.kaidoDefaultEntriesPerDollar(); // theme-setting fallback
      })();
      return this.baseRatePromise;
    }

    // Mirrors sweepstakes-entries.js getActiveRaffle (POST rpc/get_active_raffle,
    // array->data[0] else null) BUT returns a tri-state so a transient network
    // failure does NOT hide the UI. Result cached ~60s; 'error' is never cached.
    async fetchActiveRaffle() {
      if (window.KaidoPublicSnapshot) {
        await window.KaidoPublicSnapshot.ready;
        return window.KaidoPublicSnapshot.get() ? (window.KaidoPublicSnapshot.active() ? 'active' : 'none') : 'error';
      }
      const now = Date.now();
      if (this.raffleCache && now - this.raffleCache.ts < 60000) {
        return this.raffleCache.state; // 'active' | 'none'
      }
      const { url, anonKey } = this.config;
      try {
        const res = await fetch(`${url}/rest/v1/rpc/get_active_raffle`, {
          method: "POST",
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
            "Content-Type": "application/json",
          },
          body: "{}",
        });
        if (!res.ok) return "error"; // transient -> keep last-known, do NOT cache
        const data = await res.json();
        const active = Array.isArray(data) && data.length ? data[0] : null;
        const state = active ? "active" : "none";
        this.raffleCache = { state, ts: now };
        return state;
      } catch (_) {
        return "error"; // transient -> keep last-known, do NOT cache
      }
    }

    // Remove EVERY entry-estimate node (summary + per-line) across drawer AND
    // full cart page. Idempotent: no-op when nothing is present.
    clearAllEntriesDom() {
      document
        .querySelectorAll(
          ".kaido-cart-entries-total-row, .kaido-cart-line-entries-inline, .kaido-cart-line-entries-slot, .kaido-cart-line-entries",
        )
        .forEach((el) => el.remove());
    }

    // Pending-queue note shown only while paused. Idempotent — added once per
    // summary target.
    showPauseNote() {
      this.getSummaryTargets().forEach((target) => {
        if (target.querySelector(".kaido-cart-entries-paused-note")) return;
        const note = document.createElement("div");
        note.className = "kaido-cart-entries-paused-note";
        note.textContent = "Your entries will apply to the next giveaway";
        const totalContainer = target.querySelector(".cart__total-container");
        if (totalContainer) totalContainer.appendChild(note);
        else target.appendChild(note);
      });
    }

    removePauseNote() {
      document
        .querySelectorAll(".kaido-cart-entries-paused-note")
        .forEach((el) => el.remove());
    }

    async fetchProductPromotionRow(productId) {
      if (window.KaidoPublicSnapshot) return window.KaidoPublicSnapshot.product(productId);
      const cacheKey = `promo_${productId}`;
      const cached = this.promotionCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheExpiry) return cached.data;

      const { url, anonKey } = this.config;
      try {
        const fetchUrl = `${url}/rest/v1/product_promotions?shopify_product_id=eq.${encodeURIComponent(String(productId))}&is_active=eq.true&select=entries_multiplier,promotion_name,end_date,source&order=created_at.desc&limit=1`;
        const res = await fetch(fetchUrl, {
          headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` }
        });
        if (!res.ok) return null;
        const data = await res.json();
        const row = Array.isArray(data) && data.length ? data[0] : null;
        
        this.promotionCache.set(cacheKey, { data: row, timestamp: Date.now() });
        return row;
      } catch (_) {
        return null;
      }
    }

    async fetchEstimate(payload) {
      // Bypasses the Edge Function to calculate locally
      const baseRate = await this.fetchBaseRate();
      const safeBase = Number.isFinite(baseRate) && baseRate > 0 ? Math.round(baseRate) : window.kaidoDefaultEntriesPerDollar();

      let totalCartEntries = 0;
      let totalCartBase = 0;
      let totalCartBonus = 0;

      const lineItemsData = await Promise.all((payload.items || []).map(async (item) => {
        const row = await this.fetchProductPromotionRow(item.product_id);
        const multiplier = row && row.entries_multiplier ? Number(row.entries_multiplier) : 1;
        const safeMultiplier = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
        const totalRate = Math.round(safeBase * safeMultiplier);

        // Convert cart subtotal cents into standard dollars for the math
        const price = item.line_subtotal_cents / 100;

        // Apply exactly the same rounding logic as sweepstakes-entries.js
        const baseEntries = this.roundUpTo10(Math.floor(price * safeBase));
        const totalRaw = Math.ceil(price * totalRate);
        const totalEntries = this.roundUpTo10(totalRaw);
        const bonusEntries = Math.max(0, totalEntries - baseEntries);

        totalCartEntries += totalEntries;
        totalCartBase += baseEntries;
        totalCartBonus += bonusEntries;

        return {
          line_item_id: item.line_item_id,
          base_entries: baseEntries,
          bonus_entries: bonusEntries,
          total_entries: totalEntries,
          applied_multiplier: safeMultiplier
        };
      }));

      // Return identical payload schema so the UI render functions don't break
      return {
        estimated_total_entries: totalCartEntries,
        estimated_base_entries: totalCartBase,
        estimated_bonus_entries: totalCartBonus,
        promotions: [], 
        collection_upsell_hint: null,
        line_items: lineItemsData
      };
    }

    getSummaryTargets() {
      return Array.from(document.querySelectorAll(".cart__summary-totals"));
    }

    getCartRows() {
      return Array.from(
        document.querySelectorAll(".cart-items__table-row[data-key]"),
      );
    }

    needsDomRefresh() {
      const rows = this.getCartRows();
      const missingLineEntries = rows.some(
        (row) => !row.querySelector(".kaido-cart-line-entries-inline"),
      );
      const targets = this.getSummaryTargets();
      const missingSummary = targets.some(
        (target) => !target.querySelector(".kaido-cart-entries-total-row"),
      );
      return missingLineEntries || missingSummary;
    }

    ensureContainer(target) {
      let container = target.querySelector(".kaido-cart-entries-total-row");
      if (!container) {
        container = document.createElement("div");
        container.className = "kaido-cart-entries-total-row";
        const totalContainer = target.querySelector(".cart__total-container");
        if (totalContainer) totalContainer.appendChild(container);
        else target.appendChild(container);
      }
      return container;
    }

    formatMultiplier(multiplier) {
      const num = Number(multiplier || 1);
      return num % 1 === 0 ? `${num.toFixed(0)}x` : `${num.toFixed(1)}x`;
    }

    formatEntries(value) {
      return Number(value || 0).toLocaleString();
    }

    escapeHtml(input) {
      return String(input ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
    }

    formatPromotionRuleHtml(promo) {
      const labels = (promo.collections || [])
        .map((c) => {
          const id = this.escapeHtml(c?.id || "");
          const handleRaw = c?.handle ? String(c.handle) : "";
          const handle = this.escapeHtml(handleRaw);
          if (handleRaw) {
            const href = `/collections/${encodeURIComponent(handleRaw)}`;
            return `<a class="kaido-cart-entries-total-row__promo-link" href="${href}" data-collection-id="${id}">${handle}</a>`;
          }
          return `<span data-collection-id="${id}">Collection ${id}</span>`;
        })
        .join(", ");
      const qty = Number(promo.min_eligible_qty || 1);
      return `${qty}+ items from ${labels} = ${this.formatMultiplier(promo.multiplier)} on eligible items: ${labels}`;
    }

    formatUpsellHintHtml(hint) {
      if (!hint) return "";
      const message = this.escapeHtml(hint.message || "");
      const firstHandleRaw = Array.isArray(hint.collection_handles)
        ? hint.collection_handles.find((h) => String(h || "").trim().length > 0)
        : null;
      const normalizedHandle = firstHandleRaw
        ? String(firstHandleRaw)
            .trim()
            .replace(/^\/?collections\//i, "")
            .toLowerCase()
        : "";
      const cta = normalizedHandle
        ? `<a class="kaido-cart-entries-total-row__upsell-link" href="/collections/${encodeURIComponent(
            normalizedHandle,
          )}">Shop now</a>`
        : "";
      return `<div class="kaido-cart-entries-total-row__upsell"><span class="kaido-cart-entries-total-row__upsell-text">${message}</span>${cta}</div>`;
    }

    renderLineEntries(data) {
      const rows = this.getCartRows();
      if (!rows.length) return;

      rows.forEach((row) => {
        row
          .querySelectorAll(
            ".kaido-cart-line-entries-inline, .kaido-cart-line-entries, .kaido-cart-line-entries-slot",
          )
          .forEach((el) => el.remove());
      });

      const lines = Array.isArray(data?.line_items) ? data.line_items : [];
      const rowByKey = new Map();
      rows.forEach((row) => {
        const key = row.getAttribute("data-key") || "";
        if (!key) return;
        rowByKey.set(key, row);
      });

      lines.forEach((line) => {
        const lineKey = String(line?.line_item_id || "");
        if (!lineKey) return;
        const row = rowByKey.get(lineKey);
        if (!row) return;

        const priceCell = row.querySelector(".cart-items__price");
        const fallbackCell = row.querySelector(".cart-items__details");
        const targetCell = priceCell || fallbackCell;
        if (!targetCell) return;

        const total = Number(line?.total_entries || 0);
        const base = Number(line?.base_entries || 0);
        const bonus = Number(line?.bonus_entries || 0);
        const multiplier = Number(line?.applied_multiplier || 1);
        const hasBonus = bonus > 0 && multiplier > 1;

        const node = document.createElement("div");
        node.className = `kaido-cart-line-entries-inline${hasBonus ? " is-bonus" : ""}`;
        node.innerHTML = `
          <div class="kaido-cart-line-entries-inline__label-row">
            <span class="kaido-cart-line-entries-inline__label">Entries earned</span>
          </div>
          <div class="kaido-cart-line-entries-inline__amount-row">
            <span class="kaido-cart-line-entries-inline__amounts">
              ${
                hasBonus
                  ? `<span class="kaido-cart-line-entries-inline__crossed">${this.formatEntries(base)}</span>`
                  : ""
              }
              <span class="kaido-cart-line-entries-inline__value">${this.formatEntries(total)}</span>
            </span>
          </div>
          ${
            hasBonus
              ? `<div class="kaido-cart-line-entries-inline__applied">${this.formatMultiplier(
                  multiplier,
                )} applied</div>`
              : ""
          }
        `;
        if (priceCell) {
          const slot = document.createElement("div");
          slot.className = "kaido-cart-line-entries-slot";
          slot.appendChild(node);
          // Always append into a dedicated slot at the end of the price cell.
          // This avoids race conditions where fallback insertion can place entries above price.
          priceCell.appendChild(slot);
        } else {
          targetCell.insertBefore(node, targetCell.firstChild);
        }
      });
    }

    renderEstimate(data, forceRender) {
      const total = Number(data?.estimated_total_entries || 0);
      const base = Number(data?.estimated_base_entries || 0);
      const bonus = Number(data?.estimated_bonus_entries || 0);
      const promos = Array.isArray(data?.promotions) ? data.promotions : [];
      const activePromos = promos.filter((p) => p && p.qualified);
      const upsellHint = data?.collection_upsell_hint || null;
      const topPromo = activePromos.length
        ? activePromos
            .slice()
            .sort(
              (a, b) => Number(b?.multiplier || 1) - Number(a?.multiplier || 1),
            )[0]
        : null;
      const estimateKey = JSON.stringify({
        total,
        base,
        bonus,
        activePromos: activePromos.map((p) => p.promotion_id),
        lineItems: (data?.line_items || []).map((l) => [
          l?.line_item_id,
          l?.total_entries,
          l?.bonus_entries,
          l?.applied_multiplier,
        ]),
      });
      if (estimateKey === this.lastEstimateSignature && !forceRender) return;
      this.lastEstimateSignature = estimateKey;

      this.renderLineEntries(data);

      const targets = this.getSummaryTargets();
      if (!targets.length) return;

      targets.forEach((target) => {
        const container = this.ensureContainer(target);
        container.classList.toggle("is-bonus", bonus > 0);
        const topPromoRuleHtml = topPromo ? this.formatPromotionRuleHtml(topPromo) : "";
        const upsellHtml = this.formatUpsellHintHtml(upsellHint);
        const rightMetaHtml =
          bonus > 0 && topPromo
            ? `<div class="kaido-cart-entries-total-row__meta-right"><div class="kaido-cart-entries-total-row__sub-row"><span class="kaido-cart-entries-total-row__sub-amounts"><span class="kaido-cart-entries-total-row__sub-crossed">${this.formatEntries(
                base,
              )}</span><span class="kaido-cart-entries-total-row__sub-value">${this.formatEntries(
                total,
              )}</span></span></div><div class="kaido-cart-entries-total-row__sub-applied">${this.formatMultiplier(
                topPromo.multiplier,
              )} applied</div></div>`
            : `<div class="kaido-cart-entries-total-row__meta-right"><div class="kaido-cart-entries-total-row__sub-row"><span class="kaido-cart-entries-total-row__sub-amounts"><span class="kaido-cart-entries-total-row__sub-value">${this.formatEntries(
                total,
              )}</span></span></div></div>`;

        container.innerHTML = `
          <div class="kaido-cart-entries-total-row__main">
            <span class="kaido-cart-entries-total-row__label">Estimated entries</span>
            <span class="kaido-cart-entries-total-row__value">${this.formatEntries(total)}</span>
          </div>
          ${
            bonus > 0 && topPromoRuleHtml
              ? `<div class="kaido-cart-entries-total-row__promo-inline">${topPromoRuleHtml}</div>`
              : ""
          }
          ${upsellHtml}
          <div class="kaido-cart-entries-total-row__meta-row">
            <a href="/pages/entries" class="kaido-cart-entries-total-row__cta">View entries</a>
            ${rightMetaHtml}
          </div>
        `;
      });
    }

    renderError(message) {
      this.getCartRows().forEach((row) => {
        row
          .querySelectorAll(
            ".kaido-cart-line-entries-inline, .kaido-cart-line-entries, .kaido-cart-line-entries-slot",
          )
          .forEach((el) => el.remove());
      });
      this.getSummaryTargets().forEach((target) => {
        const container = this.ensureContainer(target);
        container.innerHTML = `
          <div class=\"kaido-cart-entries-total-row__main\">
            <span class=\"kaido-cart-entries-total-row__label\">Estimated entries</span>
            <span class=\"kaido-cart-entries-total-row__value\">-</span>
          </div>
          <div class=\"kaido-cart-entries-total-row__sub\">${message}</div>
        `;
      });
    }

    async refresh() {
      if (!this.isVisible()) return;
      if (this.inFlight) {
        this.refreshQueued = true;
        return;
      }
      this.inFlight = true;
      const revision = this.cartRevision;
      let superseded = false;
      try {
        const expired = Date.now() - this.lastValidatedAt >= REVALIDATE_MS;
        if (!this.cartDirty && !expired && this.lastEstimateData && this.lastCartSignature) {
          if (this.needsDomRefresh()) this.renderEstimate(this.lastEstimateData, true);
          return;
        }
        // Consume only this revision. Later native events must survive the awaits.
        this.cartDirty = false;
        if (expired) {
          this.baseRatePromise = null;
          this.promotionCache.clear();
        }
        const raffleState = await this.fetchActiveRaffle();
        if (revision !== this.cartRevision) {
          superseded = true;
          return;
        }
        if (raffleState === "none") {
          // Giveaway paused: hide every entry number + show the queue note.
          this.clearAllEntriesDom();
          this.showPauseNote();
          // Force a full recompute+render once a raffle is active again.
          this.lastCartSignature = "";
          this.lastEstimateSignature = "";
          return; // finally{} clears inFlight
        }
        // 'active' OR 'error' -> proceed. On a transient error we keep last-known
        // behavior and do NOT hide (no-flicker rule).
        this.removePauseNote();
        const cart = await this.fetchCart();
        if (revision !== this.cartRevision) {
          superseded = true;
          return;
        }
        const payload = this.buildPayload(cart);
        const sig = this.cartSignature(payload);
        if (!expired && sig === this.lastCartSignature && this.lastEstimateData) {
          if (this.lastEstimateData && this.needsDomRefresh()) {
            this.renderEstimate(this.lastEstimateData, true);
          }
          return;
        }
        const estimate = await this.fetchEstimate(payload);
        if (revision !== this.cartRevision) {
          superseded = true;
          return;
        }
        this.lastCartSignature = sig;
        this.lastEstimateData = estimate;
        this.lastValidatedAt = Date.now();
        this.renderEstimate(estimate, this.needsDomRefresh());
      } catch (error) {
        if (revision !== this.cartRevision) {
          superseded = true;
          return;
        }
        this.cartDirty = true;
        this.lastCartSignature = "";
        this.lastEstimateSignature = "";
        this.lastEstimateData = null;
        this.baseRatePromise = null;
        this.renderError("Entries estimate unavailable right now.");
      } finally {
        this.inFlight = false;
        const queued = this.refreshQueued || superseded;
        this.refreshQueued = false;
        if (queued) this.debouncedRefresh();
      }
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    if (window.__kaidoCartEstimateInitialized) return;
    const config = getSupabaseConfig();
    if (!config) return;
    window.__kaidoCartEstimateInitialized = true;
    const widget = new CartEntriesEstimate(config);
    widget.init();
  });
})();
